"use client"

import { useEffect, useState, type CSSProperties, type ReactNode } from "react"
import { useRouter } from "next/navigation"
import { supabase } from "@/lib/supabaseClient"
import { ThemeToggle } from "@/components/sales/ThemeToggle"
import { CareerLogo } from "@/components/max/CareerLogo"
import { MonogramTile } from "@/components/max/feedShared"
import { trackCareer } from "@/lib/max/analytics"
import { useCareerPricing } from "@/components/max/useCareerPricing"
import { LaunchBadge, LaunchLockLine } from "@/components/max/LaunchBadge"
import { MarketingResponsiveStyle, MKT_MOBILE_ONLY } from "@/components/max/marketing/Responsive"

// ────────────────────────────────────────────────────────────────────────────
// /max/sample : the PUBLIC free sample feed + paywall (careers.signalmax.ai/sample).
//
// The conversion moment. /try submits into here; "Unlock my full matches" leaves
// from here to /signup. Faithful to the @signalmax/ui "Careers Sample Feed" hi-fi
// handoff: result headline with the search restated as chips, three stat tiles,
// the top-3 eligible roles free to browse, a blurred locked stack behind an
// in-flow paywall card, an email fallback, and the "why N were filtered out"
// argument.
//
// Honest-data adaptations (the handoff's numbers are placeholders; never ship a
// fabricated one):
//   - Anonymous visitors have NO fit score — fit is the unlock — so the top-3
//     cards show a LOCKED fit ring, never a made-up number.
//   - The teaser carries no per-posting deadline, so the third stat tile is the
//     real "Live roles checked" (total_active), not "Closing in 30 days".
//   - The filter-reason badges name the reason categories without fabricating a
//     per-reason count; the real filtered-out total sits in the heading.
//
// Reads the teaser result /try stored (career_teaser_result). If absent but the
// search is stored, it re-runs the teaser; with neither, it routes to /try. A
// signed-in visitor goes straight to the app.
// ────────────────────────────────────────────────────────────────────────────

const INSET = "clamp(16px, 4vw, 48px)"
const MAX_W = 1180

interface Sample {
  company_name: string | null
  role_title: string
  career_track: string | null
  subtype: string | null
  location: string | null
  work_mode: string | null
  term_season: string | null
  term_year: number | null
  description: string | null
  description_full: string | null
  class_year_min: number | null
  class_year_max: number | null
  eligibility_evidence: string | null
  requirements: { required_skills: string[]; preferred_skills: string[]; coursework: string[] }
  apply_url: string | null
  fit_score: number | null
  fit_priority: string | null
  fit_confidence: number | null
}

// Below this model coverage, a fit score is an EARLY ESTIMATE (e.g. scored on track + major + the
// posting text, but not yet the student's courses/experience). We still rank by it, but we never
// render it as a confident "Strong match — 96%": the number shows as an estimate and the band is
// neutral. Signing up (full profile) lifts the confidence. Matches the fit engine's confidence scale.
const FIT_CONF_FLOOR = 0.5
interface Teaser {
  graduation_year: number | null
  tracks: string[]
  titles: string[]
  industries: string[]
  locations: string[]
  term: string | null
  total_active: number
  eligible_count: number
  by_track: { track: string; eligible: number }[]
  samples: Sample[]
  sample_locked_count: number
}

const TERM_LABEL: Record<string, string> = {
  summer_2027: "Summer 2027", fall_2027: "Fall 2027", spring_2028: "Spring 2028", summer_2028: "Summer 2028",
}
const termLabel = (t: string | null | undefined) => (t ? TERM_LABEL[t] || t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "")
const sampleTerm = (s: Sample) => [s.term_season && s.term_season[0].toUpperCase() + s.term_season.slice(1), s.term_year].filter(Boolean).join(" ")

function StatTile({ label, value, note, tone = "flat" }: { label: string; value: string; note: string; tone?: "up" | "flat" | "down" }) {
  return (
    <div className="smx-stat">
      <span className="smx-stat__label">{label}</span>
      <span className="smx-stat__value" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>
      <span className={`smx-stat__delta ${tone}`} style={{ fontFamily: "var(--font-mono)" }}>{note}</span>
    </div>
  )
}

function Chip({ active, dashed, href, children }: { active?: boolean; dashed?: boolean; href?: string; children: ReactNode }) {
  const style: CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 11px", fontSize: "var(--type-xs)", fontWeight: 600,
    borderRadius: "var(--radius-pill)", whiteSpace: "nowrap", textDecoration: "none",
    border: `1px ${dashed ? "dashed" : "solid"} ${active ? "var(--accent)" : "var(--border-subtle)"}`,
    background: active ? "color-mix(in srgb, var(--accent) 12%, transparent)" : "transparent",
    color: active ? "var(--accent-text)" : "var(--text-secondary)",
  }
  return href ? <a href={href} style={style}>{children}</a> : <span style={style}>{children}</span>
}

const UNLOCKS = [
  "All your matches, plus every new one, refreshed daily",
  "Score breakdowns and eligibility reasons on every role",
  "Résumé tailoring per opportunity",
  "Contacts with LinkedIn shortcuts and AI intro notes",
]
const FILTER_REASONS = ["Wrong class year", "Term mismatch", "Work authorization", "Degree required"]

export default function CareerSamplePage() {
  const router = useRouter()
  const pricing = useCareerPricing()
  const [phase, setPhase] = useState<"loading" | "ready" | "empty">("loading")
  const [teaser, setTeaser] = useState<Teaser | null>(null)
  const [email, setEmail] = useState("")
  const [emailSent, setEmailSent] = useState(false)
  const [emailBusy, setEmailBusy] = useState(false)

  // A signed-in visitor already has the full feed — send them there.
  useEffect(() => {
    let active = true
    supabase.auth.getSession().then(({ data }) => {
      if (active && data.session) window.location.replace("/feed")
    }).catch(() => {})
    return () => { active = false }
  }, [])

  // Load the teaser result /try stored; else re-run from the stored search; else /try.
  useEffect(() => {
    let active = true
    let stored: Teaser | null = null
    let search: Record<string, unknown> | null = null
    try {
      const r = localStorage.getItem("career_teaser_result")
      if (r) stored = JSON.parse(r) as Teaser
      const s = localStorage.getItem("career_teaser_state")
      if (s) search = JSON.parse(s) as Record<string, unknown>
    } catch { /* private mode: ignore */ }

    if (stored && typeof stored.eligible_count === "number") {
      setTeaser(stored)
      setPhase("ready")
      void trackCareer("sample_viewed", { eligible_count: stored.eligible_count })
      return
    }
    if (search && typeof search.graduation_year === "number") {
      // Re-run the teaser from the stored search (survives a direct load / refresh).
      fetch("/api/career/teaser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          graduation_year: search.graduation_year,
          titles: search.desired_titles || [],
          industries: search.desired_industries || [],
          locations: search.desired_locations || [],
          term: search.internship_term || "",
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          if (!active) return
          if (j?.ok && j.teaser) {
            setTeaser(j.teaser as Teaser)
            setPhase("ready")
            try { localStorage.setItem("career_teaser_result", JSON.stringify(j.teaser)) } catch { /* ignore */ }
          } else {
            setPhase("empty")
          }
        })
        .catch(() => active && setPhase("empty"))
      return
    }
    setPhase("empty")
    return () => { active = false }
  }, [])

  // With nothing to show, send the visitor to /try to run a search.
  useEffect(() => {
    if (phase === "empty") router.replace("/try")
  }, [phase, router])

  const saveEmail = async () => {
    if (!email.trim() || emailBusy) return
    setEmailBusy(true)
    try {
      let search: Record<string, unknown> = {}
      try { const s = localStorage.getItem("career_teaser_state"); if (s) search = JSON.parse(s) } catch { /* ignore */ }
      await fetch("/api/career/teaser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          graduation_year: search.graduation_year ?? teaser?.graduation_year ?? null,
          titles: search.desired_titles || teaser?.titles || [],
          industries: search.desired_industries || teaser?.industries || [],
          locations: search.desired_locations || teaser?.locations || [],
          term: search.internship_term || teaser?.term || "",
        }),
      })
      setEmailSent(true)
      void trackCareer("sample_email_captured", {})
    } catch { /* non-fatal */ } finally {
      setEmailBusy(false)
    }
  }

  if (phase !== "ready" || !teaser) {
    return <div className="theme-max smx" style={{ minHeight: "100vh", background: "var(--canvas)", display: "grid", placeItems: "center", color: "var(--text-muted)" }}>Loading your matches…</div>
  }

  const eligible = teaser.eligible_count
  const hidden = Math.max(0, teaser.total_active - teaser.eligible_count)
  const locked = teaser.sample_locked_count
  // Thousands-separated for display (e.g. 6,197). Keep the raw numbers above for math/comparisons.
  const fmt = (n: number) => n.toLocaleString("en-US")
  const eligibleFmt = fmt(eligible)
  const hiddenFmt = fmt(hidden)
  const lockedFmt = fmt(locked)
  const top = teaser.samples.slice(0, 3)
  const hasFit = top.some((s) => s.fit_score != null)
  // Only claim a precise "top match: X% fit" when at least one top role was scored at adequate
  // confidence. A low-confidence estimate must not headline as a hard number (doctrine: no false
  // certainty); the paywall falls back to the honest locked-count message instead.
  const confidentTop = top.filter((s) => s.fit_score != null && (s.fit_confidence ?? 0) >= FIT_CONF_FLOOR)
  const bestFit = confidentTop.length ? Math.max(...confidentTop.map((s) => s.fit_score ?? 0)) : null
  const restForBlur = teaser.samples.slice(3)
  const gradYear = teaser.graduation_year
  const searchChips: { label: string; active?: boolean }[] = [
    ...teaser.titles.slice(0, 3).map((t) => ({ label: t, active: true })),
    ...teaser.industries.slice(0, 2).map((i) => ({ label: i, active: true })),
    ...(teaser.term ? [{ label: termLabel(teaser.term) }] : []),
    ...(gradYear ? [{ label: `Class of ${gradYear}` }] : []),
  ]

  return (
    <div className="theme-max smx" style={{ minHeight: "100vh", background: "var(--canvas)", color: "var(--text-primary)", fontSize: "var(--type-base)", paddingBottom: "max(96px, calc(env(safe-area-inset-bottom) + 84px))" }}>
      <MarketingResponsiveStyle />
      {/* Sticky top bar */}
      <header style={{ position: "sticky", top: 0, zIndex: 30, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)", padding: `12px ${INSET}`, background: "color-mix(in srgb, var(--canvas) 90%, transparent)", backdropFilter: "blur(10px)", borderBottom: "1px solid var(--border-subtle)" }}>
        <CareerLogo variant="wordmark" href="/" />
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <ThemeToggle />
          <a href="/try" style={{ fontSize: "var(--type-sm)", fontWeight: 600, color: "var(--text-secondary)", textDecoration: "none" }}>Edit my search</a>
          <a href="/signup?next=%2Ffeed" style={{ display: "inline-flex", alignItems: "center", minHeight: 44, padding: "0 18px", fontSize: "var(--type-sm)", fontWeight: 600, borderRadius: "var(--radius-md)", background: "var(--accent)", color: "var(--on-accent, #fff)", textDecoration: "none", whiteSpace: "nowrap" }}>Unlock my full matches</a>
        </div>
      </header>

      <div style={{ maxWidth: MAX_W, margin: "0 auto", padding: `clamp(24px, 4vw, 48px) ${INSET} clamp(48px, 6vw, 88px)`, display: "flex", flexDirection: "column", gap: "var(--space-5)", animation: "ci-rise 400ms var(--ease-out) both" }}>

        {/* Result headline + search restated */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: "var(--space-4)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0 }}>
            <span style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--accent-text)", border: "1px solid var(--accent)", background: "color-mix(in srgb, var(--accent) 10%, transparent)", borderRadius: "var(--radius-pill)", padding: "5px 10px" }}>Free sample · No account</span>
            <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "clamp(26px, 3.4vw, 40px)", lineHeight: 1.08, letterSpacing: "-0.03em", textWrap: "pretty" }}>
              We found <span style={{ color: "var(--accent-text)" }}>{eligibleFmt} internships</span> you qualify for.
            </h1>
            <p style={{ margin: 0, maxWidth: "56ch", fontSize: "var(--type-sm)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)" }}>
              {hidden > 0 ? `${hiddenFmt} more were ineligible and already filtered out, so you'll never scroll past them.` : "Every posting was checked against its stated requirements before ranking."}
            </p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
            {searchChips.map((c, i) => <Chip key={i} active={c.active}>{c.label}</Chip>)}
            <Chip dashed href="/try">Edit ✎</Chip>
          </div>
        </div>

        {/* Stat tiles (real counts) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "var(--space-4)" }}>
          <StatTile label="Eligible for you" value={eligible.toLocaleString("en-US")} note="Checked against each posting" tone="up" />
          <StatTile label="Filtered out" value={hidden.toLocaleString("en-US")} note="Class year, term, or work auth" tone="flat" />
          <StatTile label="Live roles checked" value={teaser.total_active.toLocaleString("en-US")} note="Refreshed daily" tone="flat" />
        </div>

        {/* Top 3 — free to browse */}
        {top.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: "var(--space-3)" }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>Your top {top.length}, {hasFit ? "ranked by fit" : "free to browse"}</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", color: "var(--text-muted)" }}>{hasFit ? "Scored on your résumé · full feed on signup" : "Eligible for your search · refreshed daily"}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "var(--space-3)" }}>
              {top.map((o, i) => <SampleCard key={i} o={o} featured={i === 0} gradYear={gradYear} term={teaser.term} />)}
            </div>
          </div>
        )}

        {/* Blurred locked preview: rows sit in NORMAL FLOW under a gradient mask, never an overlay
            (an absolutely-positioned card in a shorter box is the overlay pitfall the handoff calls out). */}
        {locked > 0 && (
          <div style={{ position: "relative", borderRadius: "var(--radius-xl)", overflow: "hidden" }}>
            <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", filter: "blur(4px)", opacity: 0.45, pointerEvents: "none", userSelect: "none" }}>
              {Array.from({ length: 5 }).map((_, i) => {
                const s = restForBlur[i]
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-4)", borderRadius: "var(--radius-lg)", background: "var(--surface)", border: "1px solid var(--border-subtle)" }}>
                    <span style={{ flexShrink: 0, width: 34, height: 34, borderRadius: "var(--radius-md)", background: "var(--surface-sunken)" }} />
                    <span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
                      <span style={{ fontWeight: 600, fontSize: "var(--type-sm)", color: "var(--text-primary)" }}>{s ? s.role_title : ""}</span>
                      <span style={{ height: 8, width: "42%", borderRadius: 4, background: "var(--surface-sunken)" }} />
                    </span>
                    <span style={{ width: 40, height: 14, borderRadius: 4, background: "var(--surface-sunken)" }} />
                  </div>
                )
              })}
            </div>
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(180deg, color-mix(in srgb, var(--canvas) 40%, transparent), var(--canvas) 82%)" }} />
          </div>
        )}

        {/* Paywall card, in normal flow BENEATH the blurred rows. */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", alignItems: "flex-start", padding: "clamp(18px, 4vw, 32px)", borderRadius: "var(--radius-xl)", border: "1px solid var(--accent)", background: "linear-gradient(140deg, color-mix(in srgb, var(--accent) 13%, var(--surface)), var(--surface) 62%)", boxShadow: "var(--elevation-3, 0 24px 60px -20px rgba(0,0,0,0.5))" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--accent-text)" }}>{bestFit != null ? `Your top match: ${bestFit}% fit` : locked > 0 ? `${lockedFmt} more matches waiting` : "Your full ranked feed"}</span>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "clamp(22px, 2.6vw, 30px)", letterSpacing: "-0.025em", lineHeight: 1.12, textWrap: "pretty" }}>See all {eligibleFmt} ranked by fit.</h2>
          <p style={{ margin: 0, maxWidth: "46ch", fontSize: "var(--type-sm)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)" }}>{hasFit ? `Those top ${top.length} were scored on your résumé. Signup scores all ${eligibleFmt} the same way, with the gap-closers that lift each one. Your résumé and search carry over.` : `Your search and these ${eligibleFmt} matches carry over. Nothing to re-enter.`}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", width: "100%" }}>
            {UNLOCKS.map((u) => (
              <span key={u} style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start", fontSize: "var(--type-sm)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)" }}>
                <span style={{ color: "var(--status-success-text)", fontWeight: 700, flexShrink: 0 }}>✓</span>{u}
              </span>
            ))}
          </div>
          <LaunchBadge pricing={pricing} />
          <LaunchLockLine pricing={pricing} />
          <a href="/signup?next=%2Ffeed" onClick={() => void trackCareer("sample_unlock_clicked", { eligible_count: eligible, promo_active: pricing.promoActive, price_cents: pricing.effective.cents })} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%", minHeight: 52, fontSize: 16, fontWeight: 700, borderRadius: "var(--radius-md)", background: "var(--accent)", color: "var(--on-accent, #fff)", textDecoration: "none" }}>Unlock my full matches, free for {pricing.trialDays} days</a>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", color: "var(--text-muted)", lineHeight: 1.7 }}>No credit card · then {pricing.effective.label}/{pricing.effective.interval} · cancel anytime</span>
        </div>

        {/* Email fallback: input stacked above the button (mobile-first). */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-5)", borderRadius: "var(--radius-lg)", background: "var(--surface)", border: "1px solid var(--border-subtle)" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>Not ready yet?</span>
          <span style={{ fontSize: "var(--type-sm)", color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)" }}>We&apos;ll email you this sample and tell you when new roles you qualify for are posted. No card, unsubscribe anytime.</span>
          <input type="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setEmailSent(false) }} style={{ boxSizing: "border-box", width: "100%", minWidth: 0, minHeight: 48, padding: "0 14px", fontSize: "var(--type-base)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "var(--surface-sunken)", color: "var(--text-primary)", outline: "none" }} />
          <button type="button" onClick={saveEmail} disabled={emailBusy || emailSent || !email.trim()} style={{ width: "100%", minHeight: 48, padding: "0 16px", fontSize: "var(--type-sm)", fontWeight: 600, borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "transparent", color: "var(--text-primary)", cursor: emailBusy || emailSent || !email.trim() ? "default" : "pointer" }}>{emailSent ? "Sent ✓" : emailBusy ? "Sending…" : "Email me this"}</button>
        </div>

        {/* Why N roles never reached you */}
        {hidden > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--space-4)", padding: "var(--space-5)", borderRadius: "var(--radius-xl)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}>
            <span style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", minWidth: 0, flex: "1 1 280px" }}>
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "var(--type-base)" }}>Why {hiddenFmt} roles never reached you</span>
              <span style={{ fontSize: "var(--type-xs)", color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)" }}>Every posting is checked against its stated requirements before it&apos;s ranked.</span>
            </span>
            <span style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
              {FILTER_REASONS.map((r) => (
                <span key={r} className="smx-badge smx-soft t-danger" style={{ fontFamily: "var(--font-mono)" }}>{r}</span>
              ))}
            </span>
          </div>
        )}
      </div>

      <footer style={{ borderTop: "1px solid var(--border-subtle)", background: "var(--surface-sunken)" }}>
        <div style={{ maxWidth: MAX_W, margin: "0 auto", padding: `var(--space-6) ${INSET}`, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", color: "var(--text-muted)" }}>Powered by SignalMax · refreshed daily</span>
          <div style={{ display: "flex", gap: "var(--space-5)", fontSize: "var(--type-sm)" }}>
            <a href="/try" style={{ color: "var(--accent-text)", textDecoration: "none" }}>Edit my search</a>
            <a href="/" style={{ color: "var(--accent-text)", textDecoration: "none" }}>Home</a>
          </div>
        </div>
      </footer>

      {/* Sticky bottom bar: the unlock CTA, always thumb-reachable, with what is still locked. */}
      {/* Sticky unlock bar - MOBILE ONLY. On desktop the header's "Unlock my full matches"
          CTA stays in view, so this phone-style bottom bar is hidden. */}
      <div className={MKT_MOBILE_ONLY}>
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40, display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "12px clamp(16px, 4vw, 24px) max(12px, env(safe-area-inset-bottom))", background: "color-mix(in srgb, var(--surface-raised, var(--surface)) 94%, transparent)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", borderTop: "1px solid var(--border-subtle)" }}>
          <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: "var(--type-sm)", fontWeight: 600 }}>{locked > 0 ? `${locked.toLocaleString("en-US")} more matches` : `${eligible.toLocaleString("en-US")} ranked by fit`}</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)" }}>Free {pricing.trialDays}-day trial · then {pricing.effective.label}/{pricing.effective.interval}</span>
          </span>
          <a href="/signup?next=%2Ffeed" onClick={() => void trackCareer("sample_unlock_clicked", { eligible_count: eligible, source: "sticky_bar", promo_active: pricing.promoActive, price_cents: pricing.effective.cents })} style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 48, padding: "0 20px", fontWeight: 700, fontSize: 16, borderRadius: "var(--radius-md)", textDecoration: "none", background: "var(--accent)", color: "var(--on-accent, #fff)", whiteSpace: "nowrap" }}>Unlock all</a>
        </div>
      </div>
    </div>
  )
}

function EligChip({ children }: { children: ReactNode }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--type-2xs)", fontWeight: 600, padding: "2px 8px", borderRadius: "var(--radius-pill)", border: "1px solid var(--status-success-border, #4FD08A)", color: "var(--status-success-text)" }}>{children}</span>
}

// The graduation-year window as plain language (class_year_min/max are graduation YEARS).
function classYearWindow(min: number | null, max: number | null): string {
  if (min == null && max == null) return "Open to any graduation year"
  if (min != null && max != null) return min === max ? `Class of ${min}` : `Classes of ${min}–${max}`
  if (min != null) return `Class of ${min} or later`
  return `Class of ${max} or earlier`
}

const capWord = (s: string | null): string | null => (s && s !== "unknown" ? s[0].toUpperCase() + s.slice(1) : null)

function ReqChips({ label, items }: { label: string; items: string[] }) {
  if (!items.length) return null
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
      <span style={{ fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)" }}>{label}</span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {items.map((s, i) => (
          <span key={i} style={{ fontSize: "var(--type-xs)", padding: "3px 9px", borderRadius: "var(--radius-pill)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }}>{s}</span>
        ))}
      </div>
    </div>
  )
}

// One free-sample card. Collapsed: monogram + role + fit band + a 2-line snippet. Expanded: the
// ENTIRE internship record — full description, eligibility window, term/location/work mode, and
// the stated skill/coursework requirements. Only the 3-5 sample rows carry this, so there's no
// harm exposing it (per the founder). The primary CTA stays "unlock" — reading the record is
// free; the ranked full feed + résumé tailoring is the paid unlock, so we don't surface the raw
// external apply link here.
function SampleCard({ o, featured, gradYear, term }: { o: Sample; featured: boolean; gradYear: number | null; term: string | null }) {
  const [open, setOpen] = useState(false)
  // Honest band: a real fit number only when the résumé produced one; otherwise the role is
  // eligible but "not scored" for an anon visitor, never a fabricated number. And when the score
  // came from only a little of the model (low confidence — e.g. major + field + posting text, but
  // not yet the student's courses/experience), it is an EARLY ESTIMATE, never a confident "Strong
  // match": the band is neutral and the number is shown as "~N est." so we don't overclaim.
  // Missing confidence (e.g. an older stored teaser) is treated as LOW, never confident — we never
  // overclaim certainty we can't see.
  const lowConf = o.fit_score != null && (o.fit_confidence ?? 0) < FIT_CONF_FLOOR
  const band = o.fit_score == null
    ? { label: "Not scored", cls: "neutral" }
    : lowConf ? { label: "Early estimate", cls: "neutral" }
    : o.fit_score >= 85 ? { label: "Strong match", cls: "success" }
    : o.fit_score >= 70 ? { label: "Worth a look", cls: "warning" }
    : { label: "Long shot", cls: "warning" }
  const req = o.requirements || { required_skills: [], preferred_skills: [], coursework: [] }
  const hasReq = req.required_skills.length || req.preferred_skills.length || req.coursework.length
  const metaLine = [capWord(o.work_mode), o.subtype].filter(Boolean).join(" · ")
  return (
    <article style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-4)", borderRadius: "var(--radius-lg)", background: featured ? "color-mix(in srgb, var(--accent) 8%, var(--surface))" : "var(--surface)", border: `1px solid ${featured ? "var(--accent)" : "var(--border-subtle)"}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
        <MonogramTile name={o.company_name || "Unlisted"} size={42} />
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "var(--type-base)", lineHeight: 1.3 }}>{o.role_title}</span>
          <span style={{ fontSize: "var(--type-xs)", color: "var(--text-muted)" }}>{[o.company_name || "Unlisted", o.location, sampleTerm(o)].filter(Boolean).join(" · ")}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
          <span className={`smx-badge smx-soft t-${band.cls}`} style={{ whiteSpace: "nowrap", fontSize: 10 }}>{band.label}</span>
          <span
            title={lowConf ? "Early estimate — scored on your major, field and the posting text. Your full profile sharpens it on signup." : undefined}
            style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)" }}
          >{o.fit_score != null ? (lowConf ? `~${Math.round(o.fit_score)} est.` : `${Math.round(o.fit_score)} fit`) : "fit on signup"}</span>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)" }}>
        {gradYear && <EligChip>✓ Class year {gradYear}</EligChip>}
        {term && <EligChip>✓ {termLabel(term)}</EligChip>}
        <EligChip>✓ Eligible</EligChip>
      </div>

      {!open && o.description && (
        <span style={{ fontSize: "var(--type-sm)", color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{o.description}</span>
      )}

      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", paddingTop: "var(--space-1)" }}>
          {/* Eligibility block — why this role can consider the student. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}>
            <span style={{ fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)" }}>Eligibility</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2) var(--space-4)", fontSize: "var(--type-xs)", color: "var(--text-secondary)" }}>
              <span><strong style={{ color: "var(--text-primary)", fontWeight: 600 }}>Class years:</strong> {classYearWindow(o.class_year_min, o.class_year_max)}</span>
              {sampleTerm(o) && <span><strong style={{ color: "var(--text-primary)", fontWeight: 600 }}>Term:</strong> {sampleTerm(o)}</span>}
              {metaLine && <span><strong style={{ color: "var(--text-primary)", fontWeight: 600 }}>Setup:</strong> {metaLine}</span>}
            </div>
            {o.eligibility_evidence && <span style={{ fontSize: "var(--type-xs)", color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)" }}>{o.eligibility_evidence}</span>}
          </div>

          {(o.description_full || o.description) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-muted)" }}>What you'd do</span>
              <span style={{ fontSize: "var(--type-sm)", color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)", whiteSpace: "pre-wrap" }}>{o.description_full || o.description}</span>
            </div>
          )}

          {hasReq ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
              <ReqChips label="Required skills" items={req.required_skills} />
              <ReqChips label="Preferred skills" items={req.preferred_skills} />
              <ReqChips label="Relevant coursework" items={req.coursework} />
            </div>
          ) : null}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <button type="button" onClick={() => setOpen((v) => !v)} style={{ flex: "1 1 auto", minHeight: 44, padding: "0 16px", fontSize: "var(--type-sm)", fontWeight: 600, borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--text-primary)", cursor: "pointer" }}>
          {open ? "Hide details" : "See the full breakdown"}
        </button>
        {open && (
          <a href="/signup?next=%2Ffeed" style={{ flex: "1 1 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 44, padding: "0 16px", fontSize: "var(--type-sm)", fontWeight: 600, borderRadius: "var(--radius-md)", background: "var(--accent)", color: "var(--on-accent, #fff)", textDecoration: "none" }}>Unlock &amp; apply</a>
        )}
      </div>
    </article>
  )
}
